import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../core/components/Icon.tsx';
import { faArrowUpRightFromSquare, faCircle } from '@fortawesome/pro-solid-svg-icons';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@apollo/client';
import Layout from '../../core/components/Layout.tsx';
import Page from '../../core/components/Page';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import Button from '../../core/components/Button.tsx';
import { url } from '../../core/utils/link.ts';
import {
    CUSTOMER_INTEGRATION_ALPHAPLAN_ADD_PATH,
    CUSTOMER_INTEGRATION_ALPHAPLAN_PATH,
    CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_ADD_PATH,
    CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH,
    CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_ADD_PATH,
    CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_PATH,
    CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_ADD_PATH,
    CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH,
    CUSTOMER_INTEGRATION_NETSUITE_ADD_PATH,
    CUSTOMER_INTEGRATION_NETSUITE_PATH,
    CUSTOMER_INTEGRATION_ORDERWISE_ADD_PATH,
    CUSTOMER_INTEGRATION_ORDAT_ADD_PATH,
    CUSTOMER_INTEGRATION_ORDAT_PATH,
    CUSTOMER_INTEGRATION_ORDERWISE_PATH,
    CUSTOMER_INTEGRATION_QUICKBOOKS_ADD_PATH,
    CUSTOMER_INTEGRATION_QUICKBOOKS_PATH,
    CUSTOMER_INTEGRATION_SAGE100_ADD_PATH,
    CUSTOMER_INTEGRATION_SAGE100_PATH,
    CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_ADD_PATH,
    CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_PATH,
    CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_ADD_PATH,
    CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH,
    CUSTOMER_INTEGRATION_SHAREPOINT_CONNECT_PATH,
    CUSTOMER_INTEGRATION_SHAREPOINT_PATH,
    CUSTOMER_INTEGRATION_WECLAPP_ADD_PATH,
    CUSTOMER_INTEGRATION_WECLAPP_PATH,
} from '../constants.ts';
import {
    GET_BUSINESS_CENTRAL_INTEGRATIONS,
    GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS,
    GET_MICROTECH_BUERO_PLUS_INTEGRATIONS,
    GET_NETSUITE_INTEGRATIONS,
    GET_QUICKBOOKS_INTEGRATIONS,
    GET_SAGE100_INTEGRATIONS,
    GET_SAGE200PROFESSIONAL_INTEGRATIONS,
    GET_SAP_BUSINESS_ONE_INTEGRATIONS,
    GET_SHAREPOINT_INTEGRATIONS,
    GET_WECLAPP_INTEGRATIONS,
} from '../queries.ts';
import { GET_ORDERWISE_INTEGRATIONS } from './Orderwise/queries.ts';
import { GET_ORDAT_INTEGRATIONS } from './Ordat/queries.ts';
import { GET_ALPHAPLAN_INTEGRATIONS } from './Alphaplan/queries.ts';

const ConnectIcon = withIcon(faArrowUpRightFromSquare);
const ActiveDotIcon = withIcon(faCircle);
const FallbackIcon = withIcon(faBuilding);

const ErpIntegrationsPage = () => {
    const { t } = useTranslation('integrations');

    const { data: bcData } = useQuery(GET_BUSINESS_CENTRAL_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: dfoData } = useQuery(GET_DYNAMICS_FINANCE_OPERATIONS_INTEGRATIONS, {
        fetchPolicy: 'cache-and-network',
    });
    const { data: sharepointData } = useQuery(GET_SHAREPOINT_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: weclappData } = useQuery(GET_WECLAPP_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: sage100Data } = useQuery(GET_SAGE100_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: microtechBueroPlusData } = useQuery(GET_MICROTECH_BUERO_PLUS_INTEGRATIONS, {
        fetchPolicy: 'cache-and-network',
    });
    const { data: orderwiseData } = useQuery(GET_ORDERWISE_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: ordatData } = useQuery(GET_ORDAT_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: alphaplanData } = useQuery(GET_ALPHAPLAN_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: sage200Data } = useQuery(GET_SAGE200PROFESSIONAL_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: quickbooksData } = useQuery(GET_QUICKBOOKS_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: netsuiteData } = useQuery(GET_NETSUITE_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });
    const { data: sapData } = useQuery(GET_SAP_BUSINESS_ONE_INTEGRATIONS, { fetchPolicy: 'cache-and-network' });

    const ERP_INTEGRATIONS = [
        {
            name: 'Microsoft Business Central',
            description: 'Comprehensive ERP solution for modern businesses.',
            manageUrl: CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH,
            connectUrl: CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_ADD_PATH,
            count: bcData?.businessCentralIntegrations?.length ?? 0,
            logo: '/images/external_logos/business-central.png',
        },
        {
            name: 'Microsoft Dynamics 365 Finance and Operations',
            description: 'Enterprise resource planning for large organizations.',
            manageUrl: CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_PATH,
            connectUrl: CUSTOMER_INTEGRATION_DYNAMICS_FINANCE_OPERATIONS_ADD_PATH,
            count: dfoData?.dynamicsFinanceOperationsIntegrations?.length ?? 0,
            logo: null,
        },
        {
            name: 'SharePoint',
            description: 'Microsoft SharePoint integration for document management.',
            manageUrl: CUSTOMER_INTEGRATION_SHAREPOINT_PATH,
            connectUrl: CUSTOMER_INTEGRATION_SHAREPOINT_CONNECT_PATH,
            count: sharepointData?.sharepointIntegrations?.length ?? 0,
            logo: '/images/external_logos/sharepoint.png',
        },
        {
            name: 'Weclapp',
            description: 'Cloud-based ERP system for small and medium businesses.',
            manageUrl: CUSTOMER_INTEGRATION_WECLAPP_PATH,
            connectUrl: CUSTOMER_INTEGRATION_WECLAPP_ADD_PATH,
            count: weclappData?.weclappIntegrations?.length ?? 0,
            logo: '/images/external_logos/weclapp-vertikal-positive.png',
        },
        {
            name: 'Sage 100',
            description: 'Accounting and business management software for growing companies.',
            manageUrl: CUSTOMER_INTEGRATION_SAGE100_PATH,
            connectUrl: CUSTOMER_INTEGRATION_SAGE100_ADD_PATH,
            count: sage100Data?.sage100Integrations?.length ?? 0,
            logo: '/images/external_logos/Sage_Logo_Black_RGB png.webp',
        },
        {
            name: 'microtech büro+',
            description: 'German ERP solution for SMBs.',
            manageUrl: CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_PATH,
            connectUrl: CUSTOMER_INTEGRATION_MICROTECH_BUERO_PLUS_ADD_PATH,
            count: microtechBueroPlusData?.microtechBueroPlusIntegrations?.length ?? 0,
            logo: '/images/external_logos/microtech.png',
        },
        {
            name: 'Orderwise',
            description: 'ERP and warehouse management system for manufacturers and distributors.',
            manageUrl: CUSTOMER_INTEGRATION_ORDERWISE_PATH,
            connectUrl: CUSTOMER_INTEGRATION_ORDERWISE_ADD_PATH,
            count: orderwiseData?.orderwiseIntegrations?.length ?? 0,
            logo: null,
        },
        {
            name: 'ORDAT.ERP',
            description: 'German ERP for mid-sized manufacturing and trading companies.',
            manageUrl: CUSTOMER_INTEGRATION_ORDAT_PATH,
            connectUrl: CUSTOMER_INTEGRATION_ORDAT_ADD_PATH,
            count: ordatData?.ordatIntegrations?.length ?? 0,
            logo: '/images/external_logos/ordat.svg',
        },
        {
            name: 'ALPHAPLAN',
            description:
                'German ERP for SMBs: order confirmations are matched to and written back onto purchase orders.',
            manageUrl: CUSTOMER_INTEGRATION_ALPHAPLAN_PATH,
            connectUrl: CUSTOMER_INTEGRATION_ALPHAPLAN_ADD_PATH,
            count: alphaplanData?.alphaplanIntegrations?.length ?? 0,
            logo: null,
        },
        {
            name: 'Sage 200 Professional',
            description: 'Professional accounting software for mid-sized businesses.',
            manageUrl: CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_PATH,
            connectUrl: CUSTOMER_INTEGRATION_SAGE200PROFESSIONAL_ADD_PATH,
            count: sage200Data?.sage200professionalIntegrations?.length ?? 0,
            logo: '/images/external_logos/Sage_Logo_Black_RGB png.webp',
        },
        {
            name: 'QuickBooks',
            description: 'Popular accounting software for small businesses and startups.',
            manageUrl: CUSTOMER_INTEGRATION_QUICKBOOKS_PATH,
            connectUrl: CUSTOMER_INTEGRATION_QUICKBOOKS_ADD_PATH,
            count: quickbooksData?.quickbooksIntegrations?.length ?? 0,
            logo: '/images/external_logos/quickbooks.png',
        },
        {
            name: 'NetSuite',
            description: 'Oracle NetSuite ERP integration for financial management and business operations.',
            manageUrl: CUSTOMER_INTEGRATION_NETSUITE_PATH,
            connectUrl: CUSTOMER_INTEGRATION_NETSUITE_ADD_PATH,
            count: netsuiteData?.netsuiteIntegrations?.length ?? 0,
            logo: '/images/external_logos/netsuite.png',
        },
        {
            name: 'SAP Business One',
            description: 'Complete ERP solution for small to medium-sized enterprises.',
            manageUrl: CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_PATH,
            connectUrl: CUSTOMER_INTEGRATION_SAP_BUSINESS_ONE_ADD_PATH,
            count: sapData?.sapBusinessOneIntegrations?.length ?? 0,
            logo: '/images/external_logos/sap_business_one.png',
        },
    ];

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <Page.HeaderTitle>{t('erp.pageTitle')}</Page.HeaderTitle>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-3">
                    {ERP_INTEGRATIONS.map((integration) => (
                        <div
                            key={integration.connectUrl}
                            className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between rounded-lg border border-line-weak bg-surface-weak p-6"
                        >
                            <div className="flex flex-col gap-4 md:flex-row md:items-center">
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center">
                                    {integration.logo ? (
                                        <img
                                            src={integration.logo}
                                            alt={integration.name}
                                            className="max-h-10 max-w-10 object-contain"
                                        />
                                    ) : (
                                        <FallbackIcon className="text-2xl text-content-medium" />
                                    )}
                                </div>
                                <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-content-strong">{integration.name}</span>
                                    <span className="text-sm text-content-medium">{integration.description}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                {integration.count > 0 && (
                                    <span className="flex items-center gap-1.5 text-sm text-content-success-medium">
                                        <ActiveDotIcon className="text-[8px]" />
                                        {t('erp.activeCount', { count: integration.count })}
                                    </span>
                                )}

                                {integration.count > 0 ? (
                                    <Button asChild>
                                        <Link to={url(integration.manageUrl)}>{t('erp.manageButton')}</Link>
                                    </Button>
                                ) : (
                                    <Button variant="primary" asChild>
                                        <Link to={url(integration.connectUrl)}>
                                            {t('erp.connectButton')}
                                            <ConnectIcon className="ml-2" />
                                        </Link>
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default ErpIntegrationsPage;
